import React, { useEffect, useState, useRef, useCallback } from "react";
import api from "@/lib/api";
import { CheckCircle2, Circle, Loader2 } from "lucide-react";

const inputCls = "w-full bg-transparent border border-white/15 px-3 py-2.5 text-ink-50 focus:border-gold outline-none transition-colors text-sm";

export const CADASTRE_FIELDS = [
    ["comune", "Comune"], ["provincia", "Provincia"], ["foglio", "Foglio"], ["particella", "Particella"],
    ["subalterno", "Subalterno"], ["categoria", "Categoria catastale"], ["classe", "Classe"], ["consistenza", "Consistenza"],
    ["vani", "Vani"], ["superficie_catastale", "Superficie catastale (m²)"], ["rendita_catastale", "Rendita catastale (€)"],
    ["piano", "Piano"], ["interno", "Interno"], ["indirizzo_catastale", "Indirizzo catastale"], ["data_aggiornamento", "Data ultimo aggiornamento"],
];

export default function CadastreSection({ propertyId, onDraftChange, refreshSignal }) {
    const [data, setData] = useState({});
    const [saveState, setSaveState] = useState("idle"); // idle | saving | saved
    const timerRef = useRef(null);

    useEffect(() => {
        if (propertyId) {
            api.get(`/properties/${propertyId}/cadastre`).then((r) => setData(r.data || {})).catch(() => {});
        }
    }, [propertyId]);

    const persist = useCallback((next) => {
        if (!propertyId) { onDraftChange?.(next); return; }
        setSaveState("saving");
        clearTimeout(timerRef.current);
        timerRef.current = setTimeout(async () => {
            try { await api.put(`/properties/${propertyId}/cadastre`, next); setSaveState("saved"); }
            catch { setSaveState("idle"); }
        }, 800);
    }, [propertyId, onDraftChange]);

    const change = (k, v) => {
        const next = { ...data, [k]: v };
        setData(next);
        persist(next);
    };

    return (
        <div data-testid="cadastre-section" className="space-y-5">
            {propertyId && <CompletenessBar propertyId={propertyId} refreshSignal={`${refreshSignal}|${saveState}`} />}
            <div className="flex items-center justify-between">
                <p className="text-xs text-ink-400">Dati identificativi dell'immobile al Catasto. Visibili solo a te{propertyId ? " — salvataggio automatico" : ""}.</p>
                {propertyId && saveState !== "idle" && (
                    <span data-testid="cadastre-save-state" className="text-[11px] flex items-center gap-1.5 text-ink-400">
                        {saveState === "saving" ? <><Loader2 size={11} className="animate-spin" /> Salvataggio...</> : <><CheckCircle2 size={11} className="text-green-400" /> Salvato</>}
                    </span>
                )}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {CADASTRE_FIELDS.map(([k, label]) => (
                    <div key={k} className={k === "indirizzo_catastale" ? "col-span-2" : ""}>
                        <label className="block text-[10px] text-ink-400 mb-1.5 tracking-wider uppercase">{label}</label>
                        {k === "data_aggiornamento" ? (
                            <input data-testid={`cad-${k}`} type="date" value={data[k] || ""} onChange={(e) => change(k, e.target.value)} className={inputCls} />
                        ) : (
                            <input data-testid={`cad-${k}`} value={data[k] || ""} onChange={(e) => change(k, e.target.value)} className={inputCls} />
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}

export function CompletenessBar({ propertyId, refreshSignal, compact }) {
    const [comp, setComp] = useState(null);
    const [showList, setShowList] = useState(false);

    useEffect(() => {
        const t = setTimeout(() => {
            api.get(`/properties/${propertyId}/completeness`).then((r) => setComp(r.data)).catch(() => {});
        }, 1200);
        return () => clearTimeout(t);
    }, [propertyId, refreshSignal]);

    if (!comp) return null;
    const color = comp.pct >= 80 ? "bg-green-500" : comp.pct >= 50 ? "bg-gold" : "bg-red-400";
    return (
        <div data-testid="completeness-indicator" className={compact ? "" : "border border-white/10 bg-ink-900/40 p-4"}>
            <div className="flex items-center justify-between mb-2">
                <button type="button" onClick={() => setShowList(!showList)} className="text-[11px] uppercase tracking-wider text-ink-400 hover:text-gold">
                    Completezza immobile {showList ? "▾" : "▸"}
                </button>
                <span data-testid="completeness-pct" className="font-serif text-xl text-ink-50">{comp.pct}%</span>
            </div>
            <div className="h-1.5 bg-ink-950 overflow-hidden">
                <div className={`h-full ${color} transition-all duration-700`} style={{ width: `${comp.pct}%` }} />
            </div>
            {showList && (
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                    {comp.checklist.map((c) => (
                        <div key={c.key} className="flex items-center gap-2 text-[11px]">
                            {c.done ? <CheckCircle2 size={12} className="text-green-400 shrink-0" /> : <Circle size={12} className="text-ink-600 shrink-0" />}
                            <span className={c.done ? "text-ink-300" : "text-ink-500"}>{c.label}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
